import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Description as FileIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { documentAPI, UploadedDocument } from '../../services/api';

const DocumentUpload: React.FC = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [documents, setDocuments] = useState<UploadedDocument[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadDocuments();
  }, []);

  const loadDocuments = async () => {
    setLoading(true);
    try {
      const docs = await documentAPI.getDocuments();
      setDocuments(docs);
    } catch (err) {
      console.error('Error loading documents:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        setError('Please select a PDF file');
        return;
      }
      if (file.size > 50 * 1024 * 1024) { // 50MB limit
        setError('File size must be less than 50MB');
        return;
      }
      setSelectedFile(file);
      setError(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await documentAPI.uploadDocument(selectedFile);
      setSuccess(`Successfully uploaded ${selectedFile.name}. ${result.total_cases} cases extracted.`);
      setSelectedFile(null);
      await loadDocuments();
      
      // Reset file input
      const fileInput = document.getElementById('file-input') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'processing': return 'warning';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <SuccessIcon color="success" />;
      case 'processing': return <CircularProgress size={20} />;
      case 'failed': return <ErrorIcon color="error" />;
      default: return <FileIcon />;
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <Box sx={{ height: '100%', overflow: 'auto', p: 3 }}>
      {/* Header */}
      <Paper elevation={0} sx={{ 
        p: 4, 
        mb: 4, 
        background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)', 
        color: 'white',
        borderRadius: 3,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          background: `
            radial-gradient(circle at 20% 80%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.08) 0%, transparent 50%)
          `,
        }
      }}>
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Box sx={{ fontSize: '2.5rem' }}>📄</Box>
            <Box>
              <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                Document Upload
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 400 }}>
                Upload PDF documents for AI processing and similarity search
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Upload Section */}
      <Paper elevation={0} sx={{ 
        p: 4, 
        mb: 4,
        borderRadius: 3,
        border: '1px solid rgba(0, 0, 0, 0.06)',
      }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
          Upload New Document
        </Typography>
        
        <Box sx={{ 
          border: '3px dashed', 
          borderColor: selectedFile ? 'success.main' : 'grey.300', 
          borderRadius: 3, 
          p: 6, 
          textAlign: 'center', 
          mb: 3,
          backgroundColor: selectedFile ? 'rgba(5, 150, 105, 0.05)' : 'rgba(0, 0, 0, 0.02)',
          transition: 'all 0.3s ease',
          '&:hover': {
            borderColor: 'primary.main',
            backgroundColor: 'rgba(37, 99, 235, 0.05)',
          }
        }}>
          <UploadIcon sx={{ 
            fontSize: 64, 
            color: selectedFile ? 'success.main' : 'grey.400', 
            mb: 2,
            transition: 'color 0.3s ease'
          }} />
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
            {selectedFile ? selectedFile.name : 'Drop PDF here or click to select'}
          </Typography>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            {selectedFile 
              ? `File size: ${formatFileSize(selectedFile.size)}`
              : 'Upload veterinary case documents for AI analysis'
            }
          </Typography>
          
          <input
            id="file-input"
            type="file"
            accept=".pdf"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          <label htmlFor="file-input">
            <Button variant="outlined" component="span" sx={{ mt: 2 }}>
              Choose File
            </Button>
          </label>
        </Box>

        {selectedFile && (
          <Card sx={{ mb: 3, bgcolor: 'grey.50' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <FileIcon color="primary" />
                  <Box>
                    <Typography variant="subtitle1">
                      {selectedFile.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatFileSize(selectedFile.size)}
                    </Typography>
                  </Box>
                </Box>
                <Button
                  variant="contained"
                  onClick={handleUpload}
                  disabled={uploading}
                  startIcon={uploading ? <CircularProgress size={20} /> : <UploadIcon />}
                >
                  {uploading ? 'Uploading...' : 'Upload'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}
      </Paper>

      {/* Documents List */}
      <Paper elevation={2} sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">
            Uploaded Documents ({documents.length})
          </Typography>
          <IconButton onClick={loadDocuments} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : documents.length === 0 ? (
          <Box sx={{ textAlign: 'center', p: 3, color: 'text.secondary' }}>
            <Typography variant="body1">
              No documents uploaded yet
            </Typography>
          </Box>
        ) : (
          <List>
            {documents.map((doc, index) => (
              <React.Fragment key={doc.id}>
                <ListItem
                  sx={{
                    bgcolor: 'background.paper',
                    borderRadius: 1,
                    mb: 1,
                    border: 1,
                    borderColor: 'grey.200',
                  }}
                >
                  <ListItemIcon>
                    {getStatusIcon(doc.status)}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle1">
                          {doc.original_filename}
                        </Typography>
                        <Chip 
                          label={doc.status} 
                          size="small" 
                          color={getStatusColor(doc.status) as any}
                        />
                      </Box>
                    }
                    secondary={
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Uploaded: {formatDate(doc.upload_date)}
                        </Typography>
                        {doc.total_cases_extracted > 0 && (
                          <Typography variant="body2" color="success.main">
                            {doc.total_cases_extracted} cases extracted
                          </Typography>
                        )}
                        {doc.error_message && (
                          <Typography variant="body2" color="error.main">
                            Error: {doc.error_message}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                  {doc.status === 'processing' && (
                    <LinearProgress sx={{ width: 100, mr: 2 }} />
                  )}
                </ListItem>
                {index < documents.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>
    </Box>
  );
};

export default DocumentUpload; 